import {
  integer,
  text,
  boolean,
  date,
  uuid,
  pgTable,
  timestamp,
  uniqueIndex,
  unique,
  index,
  jsonb,
  pgEnum
} from "drizzle-orm/pg-core";
import type {
  Localized,
  StoryCard,
  StorySource,
  WomanJobStatus,
  WomanStoryStatus,
} from "../schemas/woman-story";

// Rich-text (TipTap / ProseMirror) document type — used by Post and Comment content columns.
export type RichTextDoc = {
  type: 'doc';
  content: RichTextNode[];
};

export type RichTextNode = {
  type: string;
  attrs?: Record<string, unknown>;
  content?: RichTextNode[];
  text?: string;
  marks?: Array<{ type: string; attrs?: Record<string, unknown> }>;
};

// Enums
export const communityRoleEnum = pgEnum('community_role', ['admin', 'moderator', 'member']);

// Base pgTables
// Per-user feature flags stored on the users row. A flag is on for a user when
// their row says so; when the row has no entry for a key, FEATURE_FLAG_DEFAULTS
// decides. There is no admin/permission override — admins see exactly what
// their own row (plus the defaults) allows (resolved client-side in @org/ui).
export const FEATURE_FLAG_KEYS = ["zine-class-page", "journey", "journey-products", "coordination-panel", "mellinai"] as const;
export type FeatureFlagKey = (typeof FEATURE_FLAG_KEYS)[number];
export type FeatureFlags = Partial<Record<FeatureFlagKey, boolean>>;
// Value used when `users.feature_flags` has no entry for the key.
export const FEATURE_FLAG_DEFAULTS: Record<FeatureFlagKey, boolean> = {
  "zine-class-page": false,
  "journey": false,
  "journey-products": false,
  // Pedagogical coordination panel in the teacher app (all teachers' at-risk students).
  "coordination-panel": false,
  // MellinAI free conversation practice page in the student app (/mellinai).
  "mellinai": false,
};

export const USER_TYPES = ["group", "private", "self-learning", "community", "b2b"] as const;
export type UserType = (typeof USER_TYPES)[number];

// The platform is built women-first: feminine addressing is the default and
// `gender` only switches individual students (b2b cohorts) to masculine forms.
export const GENDERS = ["female", "male"] as const;
export type Gender = (typeof GENDERS)[number];

export const IDENTITY_STATUSES = ["unverified", "verified", "needs_review", "declined"] as const;
export type IdentityStatus = (typeof IDENTITY_STATUSES)[number];
export const IDENTITY_PHOTO_KINDS = ["selfie", "id"] as const;
export type IdentityPhotoKind = (typeof IDENTITY_PHOTO_KINDS)[number];
export type IdentityAiVerdict = {
  verdict: "approve" | "reject" | "unsure";
  confidence: number;
  reason: string;
  model: string;
};
export const SIGNUP_SOURCES = ["invite", "self"] as const;
export type SignupSource = (typeof SIGNUP_SOURCES)[number];

export const User = pgTable("users", {
  userId: uuid("id").defaultRandom().primaryKey().notNull(),
  firstName: text("first_name").notNull(),
  lastName: text("last_name").notNull(),
  email: text("email").notNull(),
  phone: text("phone"),
  nickname: text("nickname"),
  // Native language (drives the AI tutor's explanations). Brazilian school: pt.
  language: text("language").$type<'en' | 'pt'>().default('pt').notNull(),
  // On-screen language the user picked in the sidebar toggle. NULL = never
  // chosen; the apps then derive a default from the level (see @org/ui
  // `resolveInitialLocale`).
  uiLanguage: text("ui_language").$type<'en' | 'pt'>(),
  isAdmin: boolean("is_admin").default(false),
  isStudent: boolean("is_student").default(true),
  isTeacher: boolean("is_teacher").default(false),
  levelId: uuid("level_id").references(() => Level.levelId),
  // Learning path this student follows; NULL = the default path.
  pathId: uuid("path_id").references(() => LearningPath.pathId),
  profileImageUrl: text("profile_image_url"),
  status: text("status").$type<"new" | "invited" | "awaiting-terms" | "joined" | "disabled">().default("new").notNull(),
  type: text("type")
    .$type<UserType>()
    .default("group")
    .notNull(),
  gender: text("gender").$type<Gender>().default("female").notNull(),
  enabled: boolean("enabled").default(false).notNull(),
  // Self sign-up identity validation (self-learning students who arrived on
  // their own through Google). Invited students are 'verified' from the start.
  // 'unverified' = has not sent a photo yet; 'needs_review' = the AI could not
  // confirm (or said no) and an admin decides by hand; 'declined' = admin said no.
  // Only the summary lives here; each photo check is a row in `identity_checks`.
  identityStatus: text("identity_status").$type<IdentityStatus>().default("verified").notNull(),
  // 'self' = created themselves via the public sign-up; 'invite' = admin-created.
  signupSource: text("signup_source").$type<SignupSource>().default("invite").notNull(),
  // ASAAS customer id (one per user, created on first checkout).
  asaasCustomerId: text("asaas_customer_id"),
  // Community profile fields
  displayName: text("display_name"),
  bio: text("bio"),
  location: text("location"),
  languagesSpoken: text("languages_spoken").array(),
  interests: text("interests").array(),
  learningGoals: text("learning_goals"),
  socialMediaLinks: jsonb("social_media_links").$type<{
    instagram?: string;
    linkedin?: string;
    website?: string;
  }>(),
  featureFlags: jsonb("feature_flags").$type<FeatureFlags>(),
  points: integer("points").default(0).notNull(),
  communityLevel: integer("community_level").default(1).notNull(),
  communityRole: communityRoleEnum("community_role").default('member').notNull(),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
});

export type UserSelect = typeof User.$inferSelect;

export const Country = pgTable("countries", {
  countryId: uuid("id").defaultRandom().primaryKey().notNull(),
  name: text("name"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
});

export type CountrySelect = typeof Country.$inferSelect;

export const State = pgTable("states", {
  stateId: uuid("id").defaultRandom().primaryKey().notNull(),
  name: text("name"),
  countryId: uuid("countryId").references(() => Country.countryId),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
});

export type StateSelect = typeof State.$inferSelect;

export const Person = pgTable("persons", {
  personId: uuid("id").defaultRandom().primaryKey().notNull(),
  userId: uuid("user_id").references(() => User.userId),
  address: text("address"),
  city: text("city"),
  zipCode: text("zip_code"),
  countryId: uuid("countryId").references(() => Country.countryId),
  stateId: uuid("stateId").references(() => State.stateId),
  nationality: text("nationality"),
  dateOfBirth: date("date_of_birth", { mode: "string" }),
  profession: text("profession"),
  educationLevel: text("education_level"),
  socialLinks: text("social_links"),
  bio: text("bio"),
  leveling: text("leveling"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
});

export type PersonSelect = typeof Person.$inferSelect;

export const Level = pgTable("levels", {
  levelId: uuid("id").defaultRandom().primaryKey().notNull(),
  name: text("name"),
  description: text("description"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
});

export type LevelSelect = typeof Level.$inferSelect;

export const Group = pgTable("groups", {
  groupId: uuid("id").defaultRandom().primaryKey().notNull(),
  description: text("description"),
  startDate: date("start_date", { mode: "string" }),
  endDate: date("end_date", { mode: "string" }),
  levelId: uuid("level_id").references(() => Level.levelId),
  teacherId: uuid("teacher_id").references(() => User.userId),
  // Learning path this cohort follows; NULL = the default path.
  pathId: uuid("path_id").references(() => LearningPath.pathId),
  isActive: boolean("is_active").default(true).notNull(),
  recordingsFolderUrl: text("recordings_folder_url"),
  classNotesUrl: text("class_notes_url"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
});

export type GroupSelect = typeof Group.$inferSelect;

export const Journey = pgTable("journeys", {
  journeyId: uuid("id").defaultRandom().primaryKey().notNull(),
  description: text("description"),
  name: text("name"),
  levelId: uuid("level_id").references(() => Level.levelId),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
});

export type JourneySelect = typeof Journey.$inferSelect;

// A learning path is an ordered list of journeys (see LearningPathXJourney in
// relationships.ts). Exactly one path is the default, enforced by the partial
// unique index `learning_paths_one_default_idx` (hand-written in the migration).
export const LearningPath = pgTable("learning_paths", {
  pathId: uuid("id").defaultRandom().primaryKey().notNull(),
  name: text("name").notNull(),
  description: text("description"),
  isDefault: boolean("is_default").default(false).notNull(),
  isActive: boolean("is_active").default(true).notNull(),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
});

export type LearningPathSelect = typeof LearningPath.$inferSelect;

// `kind`: 'journey' sells exactly ONE journey (`journeyId` set); 'base' is the
// base access — the whole default learning path, paid month by month
// (`journeyId` NULL, `billing` 'monthly'). There is one active base product.
// `trialDays` (base only) = length of the free trial a self-learning student
// gets once their identity is validated; NULL = no trial.
export const PRODUCT_KINDS = ["journey", "base"] as const;
export type ProductKind = (typeof PRODUCT_KINDS)[number];
export const PRODUCT_BILLINGS = ["one_time", "monthly"] as const;
export type ProductBilling = (typeof PRODUCT_BILLINGS)[number];

// A product sells ONE journey (kind 'journey') or the base access (kind 'base').
// `defaultDurationDays` NULL = access never expires; `priceCents` 0 = free.
// `stripePriceId` is a legacy column (payments are ASAAS).
// The "poster" content (cover, gallery, tagline, audience, outcomes, teacher
// note) is all optional; everything else the store shows (level, units, the
// women, class counts) is derived from the journey.
export const Product = pgTable("products", {
  productId: uuid("id").defaultRandom().primaryKey().notNull(),
  name: text("name").notNull(),
  description: text("description"),
  // Poster image; NULL = the tagline is rendered on a coloured background.
  coverImageUrl: text("cover_image_url"),
  // 0–4 extra images for the product page gallery.
  galleryImageUrls: jsonb("gallery_image_urls").$type<string[]>(),
  // Poster headline.
  tagline: text("tagline"),
  // "Para quem é".
  audience: text("audience"),
  // "O que você vai conquistar" — 0–8 lines.
  outcomes: jsonb("outcomes").$type<string[]>(),
  // "Uma palavra da professora" — shown only when present.
  teacherNote: text("teacher_note"),
  kind: text("kind").$type<ProductKind>().default("journey").notNull(),
  billing: text("billing").$type<ProductBilling>().default("one_time").notNull(),
  trialDays: integer("trial_days"),
  // NULL only for kind 'base'.
  journeyId: uuid("journey_id").references(() => Journey.journeyId),
  defaultDurationDays: integer("default_duration_days"),
  priceCents: integer("price_cents").default(0).notNull(),
  currency: text("currency").default("BRL").notNull(),
  stripePriceId: text("stripe_price_id"),
  isActive: boolean("is_active").default(true).notNull(),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
}, (table) => ({
  journeyIdx: index("products_journey_idx").on(table.journeyId),
}));

export type ProductSelect = typeof Product.$inferSelect;

// 'trial' = the free trial of the base product; 'asaas' = paid through ASAAS.
export const PURCHASE_SOURCES = ["stripe", "admin", "trial", "asaas"] as const;
export type PurchaseSource = (typeof PURCHASE_SOURCES)[number];

// `pending` = the student asked for access and an admin has not answered yet;
// `granted` = access is (or was) open; `declined` = the admin said no;
// `cancelled` = withdrawn. Existing rows are all `granted`.
export const PURCHASE_STATUSES = ["pending", "granted", "declined", "cancelled"] as const;
export type PurchaseStatus = (typeof PURCHASE_STATUSES)[number];

// Ledger row. Granting a product writes one of these (status 'granted') plus
// a user_journeys access row (source='purchase', purchase_id). A student's
// access request is also one of these, created as 'pending' and later moved
// to 'granted'/'declined' by an admin (the only edit a row ever gets).
export const Purchase = pgTable("purchases", {
  purchaseId: uuid("id").defaultRandom().primaryKey().notNull(),
  userId: uuid("user_id").references(() => User.userId).notNull(),
  productId: uuid("product_id").references(() => Product.productId).notNull(),
  source: text("source").$type<PurchaseSource>().default("admin").notNull(),
  stripeCheckoutSessionId: text("stripe_checkout_session_id"),
  amountCents: integer("amount_cents").default(0).notNull(),
  currency: text("currency").default("BRL").notNull(),
  purchasedAt: timestamp("purchased_at", { withTimezone: true }).defaultNow().notNull(),
  // Admin who granted it (NULL for Stripe purchases).
  grantedByUserId: uuid("granted_by_user_id").references(() => User.userId),
  note: text("note"),
  status: text("status").$type<PurchaseStatus>().default("granted").notNull(),
  // Optional message the student wrote when requesting access.
  requestNote: text("request_note"),
  // When and by which admin a pending request was approved/declined.
  resolvedAt: timestamp("resolved_at", { withTimezone: true }),
  resolvedByUserId: uuid("resolved_by_user_id").references(() => User.userId),
  // Base-access entitlement window (kind 'base' products only; journey products
  // keep their window on user_journeys). NULL = never expires. A trial sets it
  // to now + trial_days; each confirmed ASAAS payment moves it forward a month.
  accessEndsAt: timestamp("access_ends_at", { withTimezone: true }),
  // ASAAS links for paid purchases.
  asaasSubscriptionId: text("asaas_subscription_id"),
  asaasPaymentId: text("asaas_payment_id"),
  // Last status ASAAS reported (PENDING, CONFIRMED, RECEIVED, OVERDUE, ...).
  asaasStatus: text("asaas_status"),
  // Hosted ASAAS invoice/checkout page the student pays on.
  checkoutUrl: text("checkout_url"),
}, (table) => ({
  userIdx: index("purchases_user_idx").on(table.userId),
  asaasSubscriptionIdx: index("purchases_asaas_subscription_idx").on(table.asaasSubscriptionId),
}));

// Every webhook call ASAAS makes, stored before it is handled so a repeated
// delivery (same ASAAS event id) is ignored and a failed one can be replayed.
export const AsaasWebhookEvent = pgTable("asaas_webhook_events", {
  eventId: text("id").primaryKey().notNull(),
  eventType: text("event_type").notNull(),
  payload: jsonb("payload").$type<Record<string, unknown>>().notNull(),
  receivedAt: timestamp("received_at", { withTimezone: true }).defaultNow().notNull(),
  processedAt: timestamp("processed_at", { withTimezone: true }),
  error: text("error"),
});

export type AsaasWebhookEventSelect = typeof AsaasWebhookEvent.$inferSelect;

// One row per identity photo a self sign-up sent (selfie or ID). The photo,
// the AI's answer and the human decision stay here — sensitive data with its
// own retention — while `users.identity_status` keeps only the summary.
export const IdentityCheck = pgTable("identity_checks", {
  checkId: uuid("id").defaultRandom().primaryKey().notNull(),
  userId: uuid("user_id").references(() => User.userId, { onDelete: "cascade" }).notNull(),
  // The photo, kept in the media bucket (decision 2026-10-27).
  photoUrl: text("photo_url").notNull(),
  photoKind: text("photo_kind").$type<IdentityPhotoKind>().notNull(),
  // What the AI said: { verdict, confidence, reason, model }.
  aiVerdict: jsonb("ai_verdict").$type<IdentityAiVerdict>(),
  // Status the student landed on right after this check.
  outcome: text("outcome").$type<IdentityStatus>().notNull(),
  // false when the AI could not be asked: the student is not charged a try.
  countsAsAttempt: boolean("counts_as_attempt").default(true).notNull(),
  checkedAt: timestamp("checked_at", { withTimezone: true }).defaultNow().notNull(),
  // Filled when an admin decided by hand on this check.
  reviewDecision: text("review_decision").$type<"approve" | "decline">(),
  reviewedByUserId: uuid("reviewed_by_user_id").references(() => User.userId),
  reviewedAt: timestamp("reviewed_at", { withTimezone: true }),
}, (table) => ({
  userIdx: index("identity_checks_user_idx").on(table.userId, table.checkedAt),
}));

export type IdentityCheckSelect = typeof IdentityCheck.$inferSelect;

export type PurchaseSelect = typeof Purchase.$inferSelect;

// A woman the students "meet" on the journey. Managed on her own (admin menu
// "Women"); units then point at her through `units.woman_id`. She has ONE
// story: an ordered JSON list of cards (shapes in ../schemas/woman-story.ts).
// The story's first (reveal) and last (closing) pages are fixed and rendered
// by the viewer, so they are not in `storyCards`. Students only see stories
// with `storyStatus = 'published'`.
export const Woman = pgTable("women", {
  womanId: uuid("id").defaultRandom().primaryKey().notNull(),
  name: text("name").notNull(),
  years: text("years"),
  // English / Portuguese pairs.
  field: text("field"),
  fieldPt: text("field_pt"),
  nationality: text("nationality"),
  nationalityPt: text("nationality_pt"),
  symbol: text("symbol"),
  portraitUrl: text("portrait_url"),
  // Photo the AI portrait is generated from.
  referencePhotoUrl: text("reference_photo_url"),
  quote: text("quote"),
  quotePt: text("quote_pt"),
  quoteSource: text("quote_source"),
  // The seal/badge word, in Portuguese (was units.badge_name) + its English pair.
  characteristic: text("characteristic"),
  characteristicEn: text("characteristic_en"),
  tagline: jsonb("tagline").$type<Localized>(),
  storyCards: jsonb("story_cards").$type<StoryCard[]>().default([]).notNull(),
  storyStatus: text("story_status").$type<WomanStoryStatus>().default("draft").notNull(),
  // Research sources the AI draft was based on.
  sources: jsonb("sources").$type<StorySource[]>(),
  // AI jobs (agent-events worker): portrait generation and profile/story draft.
  portraitStatus: text("portrait_status").$type<WomanJobStatus>().default("idle").notNull(),
  portraitError: text("portrait_error"),
  draftStatus: text("draft_status").$type<WomanJobStatus>().default("idle").notNull(),
  draftError: text("draft_error"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
});

export type WomanSelect = typeof Woman.$inferSelect;
export type WomanInsert = typeof Woman.$inferInsert;

export const Unit = pgTable("units", {
  unitId: uuid("id").defaultRandom().primaryKey().notNull(),
  name: text("name"),
  description: text("description"),
  // The woman this unit introduces; NULL = none picked yet.
  womanId: uuid("woman_id").references(() => Woman.womanId, { onDelete: 'set null' }),
  /** @deprecated moved to women */
  womanName: text("woman_name"),
  /** @deprecated moved to women */
  womanYears: text("woman_years"),
  /** @deprecated moved to women */
  womanField: text("woman_field"),
  /** @deprecated moved to women */
  womanSymbol: text("woman_symbol"),
  /** @deprecated moved to women */
  womanPortraitUrl: text("woman_portrait_url"),
  /** @deprecated moved to women (characteristic) */
  badgeName: text("badge_name"),
  empowermentPoints: integer("empowerment_points").default(0),
  /** @deprecated moved to women */
  quote: text("quote"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
}, (table) => ({
  womanIdx: index("units_woman_idx").on(table.womanId),
}));

export type UnitSelect = typeof Unit.$inferSelect;

export const LearningProgress = pgTable("learning_progress", {
  learningProgressId: uuid("id").defaultRandom().primaryKey().notNull(),
  userId: uuid("user_id").references(() => User.userId, { onDelete: 'cascade' }).notNull(),
  journeyId: uuid("journey_id").references(() => Journey.journeyId, { onDelete: 'cascade' }).notNull(),
  unitId: uuid("unit_id").references(() => Unit.unitId, { onDelete: 'cascade' }).notNull(),
  classId: uuid("class_id").references(() => Class.classId, { onDelete: 'cascade' }),
  status: text("status").$type<"not_started" | "in_progress" | "completed">().default("not_started").notNull(),
  startedAt: timestamp("started_at", { mode: "date" }),
  lastAccessedAt: timestamp("last_accessed_at", { mode: "date" }),
  completedAt: timestamp("completed_at", { mode: "date" }),
  badgeClaimedAt: timestamp("badge_claimed_at", { mode: "date" }),
  // When the student first saw the unit's woman story (unit-level row only).
  storySeenAt: timestamp("story_seen_at", { mode: "date", withTimezone: true }),
  // When the student met the unit's arrival banner on the journey wall and
  // sent the poster down onto the wall (unit-level row only).
  arrivalSeenAt: timestamp("arrival_seen_at", { mode: "date", withTimezone: true }),
  override: boolean("override").default(false).notNull(),
}, (table) => ({
  // NULLS NOT DISTINCT so unit-level rows (class_id IS NULL) are unique too.
  uniqueProgress: unique("learning_progress_user_journey_unit_class_unique")
    .on(table.userId, table.journeyId, table.unitId, table.classId)
    .nullsNotDistinct(),
}));

export type LearningProgressSelect = typeof LearningProgress.$inferSelect;

export const Class = pgTable("classes", {
  classId: uuid("id").defaultRandom().primaryKey().notNull(),
  name: text("name"),
  description: text("description"),
  levelId: uuid("level_id").references(() => Level.levelId),
  imageUrl: text("image_url"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
});

export type ClassSelect = typeof Class.$inferSelect;

// Table: exercises
export const Exercise = pgTable("exercises", {
  exerciseId: uuid("id").defaultRandom().primaryKey().notNull(),
  contentId: uuid("content_id").references(() => Content.contentId, { onDelete: 'cascade' }).notNull(),
  exerciseType: text("exercise_type").$type<
    | "MultipleChoice"
    | "Written"
    | "Recorded"
    | "FillInBlank"
    | "SelectForBlank"
    | "Conversational"
    | "Matching"
    | "Scramble"
    | "FileUpload"
  >().notNull().default("MultipleChoice"),
  title: text("title"), // Optional: To name the exercise
  description: text("description"), // Markdown or plain-text instructions
  correction: text("correction"), // Guidance for how to evaluate submissions
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
});

export type ExerciseSelect = typeof Exercise.$inferSelect;

// Exercise submission tracking
export const ExerciseSubmission = pgTable("exercise_submissions", {
  submissionId: uuid("id").defaultRandom().primaryKey().notNull(),
  exerciseId: uuid("exercise_id").references(() => Exercise.exerciseId, { onDelete: 'cascade' }).notNull(),
  userId: uuid("user_id").references(() => User.userId, { onDelete: 'cascade' }).notNull(),

  // Submission metadata
  submittedAt: timestamp("submitted_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
  attemptNumber: integer("attempt_number").notNull().default(1),

  // Response data (polymorphic based on exercise type)
  responseData: text("response_data").notNull(), // JSON field - structure varies by exercise type

  // Grading information
  status: text("status").$type<"submitted" | "grading" | "graded" | "returned">().default("submitted").notNull(),
  needsManualGrading: boolean("needs_manual_grading").notNull(),

  // Scoring
  score: integer("score"), // Nullable until graded
  maxScore: integer("max_score").notNull(),
  passingScore: integer("passing_score"),
});

export type ExerciseSubmissionSelect = typeof ExerciseSubmission.$inferSelect;

export const Checkpoint = pgTable("checkpoints", {
  checkpointId: uuid("id").defaultRandom().primaryKey().notNull(),
  name: text("name").notNull(),
  description: text("description"),
  type: text("type").$type<"unit" | "journey">().notNull().default("unit"),
  unitId: uuid("unit_id").references(() => Unit.unitId),
  journeyId: uuid("journey_id").references(() => Journey.journeyId),
  minimalScore: integer("minimal_score").notNull(),
  timeLimit: integer("time_limit_minutes"), // Optional time limit in minutes
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
});

export type CheckpointSelect = typeof Checkpoint.$inferSelect;


// Multiple Choice details
export const MultipleChoiceExercise = pgTable("multiple_choice_exercises", {
  multipleChoiceExerciseId: uuid("id").defaultRandom().primaryKey().notNull(),
  exerciseId: uuid("exercise_id")
    .references(() => Exercise.exerciseId, { onDelete: 'cascade' })
    .notNull(),
  question: text("question"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
});

export type MultipleChoiceExerciseSelect = typeof MultipleChoiceExercise.$inferSelect;

// Multiple Choice Options
export const MultipleChoiceOption = pgTable("multiple_choice_options", {
  multipleChoiceOptionId: uuid("id").defaultRandom().primaryKey().notNull(),
  multipleChoiceExerciseId: uuid("multiple_choice_exercise_id")
    .references(() => MultipleChoiceExercise.multipleChoiceExerciseId, { onDelete: 'cascade' })
    .notNull(),
  label: text("label").notNull(), // e.g., "2", "3", "4"
  isCorrect: boolean("is_correct").default(false),
  order: integer("order").default(0).notNull(),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
});

export type MultipleChoiceOptionSelect = typeof MultipleChoiceOption.$inferSelect;

// Fill in the blank exercises
export const FillInBlankExercise = pgTable("fill_in_blank_exercises", {
  fillInBlankExerciseId: uuid("id").defaultRandom().primaryKey().notNull(),
  exerciseId: uuid("exercise_id")
    .references(() => Exercise.exerciseId, { onDelete: 'cascade' })
    .notNull(),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
});

export type FillInBlankExerciseSelect = typeof FillInBlankExercise.$inferSelect;

export const FillInBlankExerciseSentence = pgTable("fill_in_blank_exercise_sentences", {
  fillInBlankExerciseSentenceId: uuid("id").defaultRandom().primaryKey().notNull(),
  fillInBlankExerciseId: uuid("fill_in_blank_exercise_id")
    .references(() => FillInBlankExercise.fillInBlankExerciseId, { onDelete: 'cascade' })
    .notNull(),
  sentence: text("sentence").notNull(),
  order: integer("order").default(0).notNull(),
  correction: text("correction"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
});

export type FillInBlankExerciseSentenceSelect = typeof FillInBlankExerciseSentence.$inferSelect;

export const FillInBlankSentenceBlank = pgTable("fill_in_blank_sentence_blanks", {
  fillInBlankSentenceBlankId: uuid("id").defaultRandom().primaryKey().notNull(),
  fillInBlankExerciseSentenceId: uuid("fill_in_blank_exercise_sentence_id")
    .references(() => FillInBlankExerciseSentence.fillInBlankExerciseSentenceId, { onDelete: 'cascade' })
    .notNull(),
  position: integer("position").default(0).notNull(),
  answer: text("answer").notNull(),
  correction: text("correction"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
});

export type FillInBlankSentenceBlankSelect = typeof FillInBlankSentenceBlank.$inferSelect;

// Select-for-blank exercises
export const SelectForBlankExercise = pgTable("select_for_blank_exercises", {
  selectForBlankExerciseId: uuid("id").defaultRandom().primaryKey().notNull(),
  exerciseId: uuid("exercise_id")
    .references(() => Exercise.exerciseId, { onDelete: 'cascade' })
    .notNull(),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
});

export type SelectForBlankExerciseSelect = typeof SelectForBlankExercise.$inferSelect;

export const SelectForBlankSentence = pgTable("select_for_blank_sentences", {
  selectForBlankSentenceId: uuid("id").defaultRandom().primaryKey().notNull(),
  selectForBlankExerciseId: uuid("select_for_blank_exercise_id")
    .references(() => SelectForBlankExercise.selectForBlankExerciseId, { onDelete: 'cascade' })
    .notNull(),
  sentence: text("sentence").notNull(),
  order: integer("order").default(0).notNull(),
  correction: text("correction"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
});

export type SelectForBlankSentenceSelect = typeof SelectForBlankSentence.$inferSelect;

export const SelectForBlankSentenceBlank = pgTable("select_for_blank_sentence_blanks", {
  selectForBlankSentenceBlankId: uuid("id").defaultRandom().primaryKey().notNull(),
  selectForBlankSentenceId: uuid("select_for_blank_sentence_id")
    .references(() => SelectForBlankSentence.selectForBlankSentenceId, { onDelete: 'cascade' })
    .notNull(),
  position: integer("position").default(0).notNull(),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
});

export type SelectForBlankSentenceBlankSelect = typeof SelectForBlankSentenceBlank.$inferSelect;

export const SelectForBlankOption = pgTable("select_for_blank_options", {
  selectForBlankOptionId: uuid("id").defaultRandom().primaryKey().notNull(),
  selectForBlankSentenceBlankId: uuid("select_for_blank_sentence_blank_id")
    .references(() => SelectForBlankSentenceBlank.selectForBlankSentenceBlankId, { onDelete: 'cascade' })
    .notNull(),
  label: text("label").notNull(),
  isCorrect: boolean("is_correct").default(false).notNull(),
  order: integer("order").default(0).notNull(),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
});

export type SelectForBlankOptionSelect = typeof SelectForBlankOption.$inferSelect;

// Written response details
export const WrittenExercise = pgTable("written_exercises", {
  writtenExerciseId: uuid("id").defaultRandom().primaryKey().notNull(),
  exerciseId: uuid("exercise_id")
    .references(() => Exercise.exerciseId, { onDelete: 'cascade' })
    .notNull(),
  format: text("format"), // e.g., "markdown", "html", "plain"
  textType: text("text_type").$type<"long" | "short">().notNull(),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
});

export type WrittenExerciseSelect = typeof WrittenExercise.$inferSelect;

// Recorded response details
export const RecordedExercise = pgTable("recorded_exercises", {
  recordedExerciseId: uuid("id").defaultRandom().primaryKey().notNull(),
  exerciseId: uuid("exercise_id")
    .references(() => Exercise.exerciseId, { onDelete: 'cascade' })
    .notNull(),
  maxDurationSec: integer("max_duration_sec").default(60),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
});

export type RecordedExerciseSelect = typeof RecordedExercise.$inferSelect;

// Conversational exercise details - for real-time AI voice conversations
export const ConversationalExercise = pgTable("conversational_exercises", {
  conversationalExerciseId: uuid("id").defaultRandom().primaryKey().notNull(),
  exerciseId: uuid("exercise_id")
    .references(() => Exercise.exerciseId, { onDelete: 'cascade' })
    .notNull(),
  theme: text("theme").notNull(), // e.g., "ordering food at a restaurant"
  maxDurationSec: integer("max_duration_sec").default(300).notNull(),
  systemPrompt: text("system_prompt"), // Custom AI behavior instructions
  targetSkills: text("target_skills"), // JSON array of skills to develop
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
});

export type ConversationalExerciseSelect = typeof ConversationalExercise.$inferSelect;

// Free conversation practice with MellinAI ("Conversação" area): the topics the
// founder curates in Admin. Levels are CEFR bands (A1..C2); a topic is offered
// to students whose band falls within [minLevel, maxLevel], and as a
// "challenge" to students one band below minLevel.
export const CONVERSATION_LEVEL_BANDS = ["A1", "A2", "B1", "B2", "C1", "C2"] as const;
export type ConversationLevelBand = (typeof CONVERSATION_LEVEL_BANDS)[number];

export const ConversationTopic = pgTable("conversation_topics", {
  conversationTopicId: uuid("id").defaultRandom().primaryKey().notNull(),
  // Stable key used by the seed migration so re-running it never duplicates rows.
  slug: text("slug").notNull().unique(),
  titleEn: text("title_en").notNull(),
  titlePt: text("title_pt").notNull(),
  descriptionEn: text("description_en").notNull(),
  descriptionPt: text("description_pt").notNull(),
  minLevel: text("min_level").$type<ConversationLevelBand>().notNull(),
  maxLevel: text("max_level").$type<ConversationLevelBand>().notNull(),
  // What the student practises in this talk, shown on her card (e.g. "simple past, telling a story").
  targetStructures: text("target_structures"),
  // Extra pedagogical guidance for MellinAI, never shown to the student.
  guidance: text("guidance"),
  isActive: boolean("is_active").default(true).notNull(),
  order: integer("order").default(0).notNull(),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
});

export type ConversationTopicSelect = typeof ConversationTopic.$inferSelect;

// Where a conversation session came from: a lesson exercise, or the free
// practice area (MellinAI) where there is no exercise, only a topic.
export const CONVERSATION_SESSION_SOURCES = ["exercise", "practice"] as const;
export type ConversationSessionSource = (typeof CONVERSATION_SESSION_SOURCES)[number];

// Conversation session - tracks each student's conversation attempt
export const ConversationSession = pgTable("conversation_sessions", {
  conversationSessionId: uuid("id").defaultRandom().primaryKey().notNull(),
  // NULL for free practice sessions (source = 'practice').
  exerciseId: uuid("exercise_id")
    .references(() => Exercise.exerciseId, { onDelete: 'cascade' }),
  userId: uuid("user_id")
    .references(() => User.userId, { onDelete: 'cascade' })
    .notNull(),
  source: text("source").$type<ConversationSessionSource>().default("exercise").notNull(),
  // Free practice only: the topic she picked (kept when the topic is later deleted).
  topicId: uuid("topic_id").references(() => ConversationTopic.conversationTopicId, { onDelete: 'set null' }),
  // Free practice only: the duration she chose, after the daily limit was applied.
  plannedDurationSec: integer("planned_duration_sec"),
  // 'evaluating' is a short-lived claim taken by endSession so two concurrent end
  // requests (timer + click) collapse into one evaluation. Plain text column, no enum.
  status: text("status").$type<"active" | "evaluating" | "completed" | "abandoned">().default("active").notNull(),
  startedAt: timestamp("started_at", { mode: "date" }).defaultNow().notNull(),
  endedAt: timestamp("ended_at", { mode: "date" }),
  evaluation: text("evaluation"), // JSON with AI evaluation results
  score: integer("score"), // Final score for the conversation
  maxScore: integer("max_score").default(100),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
});

export type ConversationSessionSelect = typeof ConversationSession.$inferSelect;

// Conversation turn - stores each message in a conversation
export const ConversationTurn = pgTable("conversation_turns", {
  turnId: uuid("id").defaultRandom().primaryKey().notNull(),
  sessionId: uuid("session_id")
    .references(() => ConversationSession.conversationSessionId, { onDelete: 'cascade' })
    .notNull(),
  role: text("role").$type<"user" | "assistant" | "system">().notNull(),
  content: text("content").notNull(),
  audioUrl: text("audio_url"), // Optional: URL to audio recording of this turn
  timestamp: timestamp("timestamp", { mode: "date" }).defaultNow().notNull(),
  order: integer("order").default(0).notNull(),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
});

export type ConversationTurnSelect = typeof ConversationTurn.$inferSelect;

// Videos
export const Video = pgTable("videos", {
  videoId: uuid("id").defaultRandom().primaryKey().notNull(),
  contentId: uuid("content_id").references(() => Content.contentId, { onDelete: 'cascade' }).notNull(),
  url: text("url").notNull(), // Storage location (e.g., S3 URL)
  ext: text("ext"),
  transcript: text("transcript"), // AI-generated transcript for supporting material context
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
});

export type VideoSelect = typeof Video.$inferSelect;

// Images
export const Image = pgTable("images", {
  imageId: uuid("id").defaultRandom().primaryKey().notNull(),
  contentId: uuid("content_id").references(() => Content.contentId, { onDelete: 'cascade' }).notNull(),
  url: text("url").notNull(), // Storage location (e.g., S3 URL)
  ext: text("ext"),
  aiDescription: text("ai_description"), // AI-generated description for supporting material context
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
});

export type ImageSelect = typeof Image.$inferSelect;

// Audios
export const Audio = pgTable("audios", {
  audioId: uuid("id").defaultRandom().primaryKey().notNull(),
  contentId: uuid("content_id").references(() => Content.contentId, { onDelete: 'cascade' }).notNull(),
  url: text("url").notNull(), // Storage location (e.g., S3 URL)
  length: integer("length"),
  transcript: text("transcript"), // AI-generated transcript for supporting material context
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
});

export type AudioSelect = typeof Audio.$inferSelect;

// Content supertype
export const Content = pgTable("contents", {
  contentId: uuid("id").defaultRandom().primaryKey().notNull(),
  title: text("title"),
  description: text("description"),
  contentType: text("content_type").$type<"text" | "image" | "video" | "audio" | "document" | "exercise">().notNull(),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
});

export type ContentSelect = typeof Content.$inferSelect;

// TextContent type
export const TextContent = pgTable("text_contents", {
  textContentId: uuid("id").defaultRandom().primaryKey().notNull(),
  contentId: uuid("content_id").references(() => Content.contentId, { onDelete: 'cascade' }).notNull(),
  text: text("text").notNull(),
  format: text("format").$type<"plain" | "markdown" | "html">().default("plain").notNull(),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
});

export type TextContentSelect = typeof TextContent.$inferSelect;

// Documents (PDFs, Word files, etc.)
export const Document = pgTable("documents", {
  documentId: uuid("id").defaultRandom().primaryKey().notNull(),
  contentId: uuid("content_id").references(() => Content.contentId, { onDelete: 'cascade' }).notNull(),
  url: text("url").notNull(), // Storage location (e.g., S3 URL)
  format: text("format").$type<"pdf" | "word" | "txt">().default("pdf").notNull(),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
});

export type DocumentSelect = typeof Document.$inferSelect;

// Block - a collection of contents that belongs to a class
export const Block = pgTable("blocks", {
  blockId: uuid("id").defaultRandom().primaryKey().notNull(),
  classId: uuid("class_id").references(() => Class.classId, { onDelete: 'cascade' }).notNull(),
  name: text("name").notNull(),
  description: text("description"),
  order: integer("order").default(0).notNull(), // For ordering blocks within a class
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
});

export type BlockSelect = typeof Block.$inferSelect;

// BlockContent - join table between Block and Content (N:M relationship)
export const BlockContent = pgTable("block_contents", {
  blockContentId: uuid("id").defaultRandom().primaryKey().notNull(),
  blockId: uuid("block_id").references(() => Block.blockId, { onDelete: 'cascade' }).notNull(),
  contentId: uuid("content_id").references(() => Content.contentId).notNull(),
  order: integer("order").default(0).notNull(), // For ordering content within a block
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
});

export type BlockContentSelect = typeof BlockContent.$inferSelect;

// export const ClassSession = pgTable("class_sessions", {
//   sessionId: uuid("id").defaultRandom().primaryKey().notNull(),
//   classId: uuid("class_id").references(() => Class.classId, { onDelete: 'cascade' }).notNull(),
//   groupId: uuid("group_id").references(() => Group.groupId), // For group sessions
//   startTime: timestamp("start_time", { mode: "date", precision: 6, withTimezone: true }).notNull(),
//   endTime: timestamp("end_time", { mode: "date", precision: 6, withTimezone: true }),
//   isCompleted: boolean("is_completed").default(false),
//   recordingUrl: text("recording_url"), // Optional recording for future reference
// });

// export type ClassSessionSelect = typeof ClassSession.$inferSelect;

// Many-to-Many relationship table (used in schemas, so defined here to avoid circular imports)
export const UserXGroup = pgTable("user_groups", {
  userId: uuid("user_id").references(() => User.userId),
  groupId: uuid("group_id").references(() => Group.groupId),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
});

export type UserXGroupSelect = typeof UserXGroup.$inferSelect;

export const EventCategory = pgTable("event_categories", {
  eventCategoryId: uuid("id").defaultRandom().primaryKey().notNull(),
  name: text("name").notNull(),
  slug: text("slug").notNull(),
  description: text("description"),
  isDefault: boolean("is_default").default(false).notNull(),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
});

export type EventCategorySelect = typeof EventCategory.$inferSelect;

export const Event = pgTable("events", {
  eventId: uuid("id").defaultRandom().primaryKey().notNull(),
  title: text("title").notNull(),
  description: text("description"),
  eventKind: text("event_kind")
    .$type<"group-class" | "private-class" | "closed-event" | "open-event" | "tutoring">()
    .notNull(),
  eventCategoryId: uuid("event_category_id")
    .references(() => EventCategory.eventCategoryId)
    .notNull(),
  startTime: timestamp("start_time", { mode: "date", precision: 6, withTimezone: true }).notNull(),
  endTime: timestamp("end_time", { mode: "date", precision: 6, withTimezone: true }).notNull(),
  location: text("location"),
  status: text("status")
    .$type<"scheduled" | "cancelled" | "completed" | "draft">()
    .default("scheduled")
    .notNull(),
  hostUserId: uuid("host_user_id").references(() => User.userId).notNull(),
  createdByUserId: uuid("created_by_user_id").references(() => User.userId).notNull(),
  isTemplate: boolean("is_template").default(false).notNull(),
  seriesTemplateId: uuid("series_template_id").references(() => Event.eventId),
  parentEventId: uuid("parent_event_id").references(() => Event.eventId),
  isRescheduled: boolean("is_rescheduled").default(false).notNull(),
  originalStartTime: timestamp("original_start_time", { mode: "date", precision: 6, withTimezone: true }),
  originalEndTime: timestamp("original_end_time", { mode: "date", precision: 6, withTimezone: true }),
  recurrenceRule: text("recurrence_rule"),
  recurrenceStartsAt: timestamp("recurrence_starts_at", { mode: "date", precision: 6, withTimezone: true }),
  recurrenceEndsAt: timestamp("recurrence_ends_at", { mode: "date", precision: 6, withTimezone: true }),
  // Flag for tracking occurrences with individually assigned groups, classes, or attendees
  // When true, this occurrence will not be overwritten by series association updates
  hasCustomAssociations: boolean("has_custom_associations").default(false).notNull(),
  // recordingUrl: text("recording_url"),
  reminderSentAt: timestamp("reminder_sent_at", { mode: "date", precision: 6, withTimezone: true }),
  classReminderSentAt: timestamp("class_reminder_sent_at", { mode: "date", precision: 6, withTimezone: true }),
  createdAt: timestamp("created_at", { mode: "date" }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { mode: "date" }).defaultNow().notNull(),
});

export type EventSelect = typeof Event.$inferSelect;

export const ChatSession = pgTable("chat_sessions", {
  sessionId: uuid("id").defaultRandom().primaryKey().notNull(),
  userId: uuid("user_id").references(() => User.userId).notNull(),
  title: text("title"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
});

export type ChatSessionSelect = typeof ChatSession.$inferSelect;

export const ChatMessage = pgTable("chat_messages", {
  messageId: uuid("id").defaultRandom().primaryKey().notNull(),
  sessionId: uuid("session_id").references(() => ChatSession.sessionId, { onDelete: 'cascade' }).notNull(),
  role: text("role").$type<"user" | "assistant" | "system">().notNull(),
  content: text("content").notNull(),
  context: text("context"), // Store relevant page context for this message
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
});

export type ChatMessageSelect = typeof ChatMessage.$inferSelect;

export const UserClassBlockHistory = pgTable("user_class_block_history", {
  blockHistoryId: uuid("id").defaultRandom().primaryKey().notNull(),
  userId: uuid("user_id").references(() => User.userId, { onDelete: 'cascade' }).notNull(),
  classId: uuid("class_id").references(() => Class.classId, { onDelete: 'cascade' }).notNull(),
  blockId: uuid("block_id").references(() => Block.blockId, { onDelete: 'cascade' }).notNull(),
  lastVisitedAt: timestamp("last_visited_at", { mode: "date" }).defaultNow().notNull(),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
});

export type UserClassBlockHistorySelect = typeof UserClassBlockHistory.$inferSelect;

export const Scorecard = pgTable("scorecards", {
  scorecardId: uuid("id").defaultRandom().primaryKey().notNull(),
  eventId: uuid("event_id").references(() => Event.eventId, { onDelete: 'cascade' }).notNull(),
  teacherId: uuid("teacher_id").references(() => User.userId, { onDelete: 'cascade' }).notNull(),
  notes: text("notes"),
  createdAt: timestamp("created_at", { mode: "date" }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { mode: "date" }).defaultNow().notNull(),
}, (table) => ({
  eventUnique: uniqueIndex("scorecards_event_id_unique").on(table.eventId),
}));

export type ScorecardSelect = typeof Scorecard.$inferSelect;

export const ScorecardPresence = pgTable("scorecard_presences", {
  scorecardPresenceId: uuid("id").defaultRandom().primaryKey().notNull(),
  scorecardId: uuid("scorecard_id").references(() => Scorecard.scorecardId, { onDelete: 'cascade' }).notNull(),
  studentId: uuid("student_id").references(() => User.userId, { onDelete: 'cascade' }).notNull(),
  isPresent: boolean("is_present").default(false).notNull(),
  studentNotes: text("student_notes"),
  createdAt: timestamp("created_at", { mode: "date" }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { mode: "date" }).defaultNow().notNull(),
}, (table) => ({
  uniquePresence: uniqueIndex("scorecard_presence_scorecard_student_unique").on(table.scorecardId, table.studentId),
}));

export type ScorecardPresenceSelect = typeof ScorecardPresence.$inferSelect;

// A teacher's own risk signal on a student ("red" / "yellow" / "green") with
// the reason in their words. Append-only: the newest row is the current flag,
// older rows are the history shown on the student profile.
export const STUDENT_FLAG_LEVELS = ['red', 'yellow', 'green'] as const;
export type StudentFlagLevel = (typeof STUDENT_FLAG_LEVELS)[number];

/** Why the teacher flagged the student (her categories, 2026-10-21). */
export const STUDENT_FLAG_REASONS = [
  'class_engagement',
  'work',
  'motivation',
  'learning_difficulty',
  'communication',
  'personal_life',
  'emotional_health',
  'family_logistics',
  'schedule_conflict',
  'other',
] as const;
export type StudentFlagReason = (typeof STUDENT_FLAG_REASONS)[number];

export const StudentFlag = pgTable("student_flags", {
  studentFlagId: uuid("id").defaultRandom().primaryKey().notNull(),
  studentId: uuid("student_id").references(() => User.userId, { onDelete: 'cascade' }).notNull(),
  teacherId: uuid("teacher_id").references(() => User.userId, { onDelete: 'cascade' }).notNull(),
  flag: text("flag").$type<StudentFlagLevel>().notNull(),
  // Nullable: flags saved before the reason categories existed have none.
  reason: text("reason").$type<StudentFlagReason>(),
  comment: text("comment").notNull(),
  createdAt: timestamp("created_at", { mode: "date", withTimezone: true }).defaultNow().notNull(),
}, (table) => [
  index("student_flags_student_created_idx").on(table.studentId, table.createdAt),
]);

export type StudentFlagSelect = typeof StudentFlag.$inferSelect;

// Which sections (blocks) of the class given in a session the teacher set as
// homework. Marked on the class record (scorecard). When a session has no
// rows here, every exercise of the class counts as homework.
export const EventHomeworkBlock = pgTable("event_homework_blocks", {
  eventHomeworkBlockId: uuid("id").defaultRandom().primaryKey().notNull(),
  eventId: uuid("event_id").references(() => Event.eventId, { onDelete: 'cascade' }).notNull(),
  blockId: uuid("block_id").references(() => Block.blockId, { onDelete: 'cascade' }).notNull(),
  createdAt: timestamp("created_at", { mode: "date", withTimezone: true }).defaultNow().notNull(),
}, (table) => [
  uniqueIndex("event_homework_blocks_event_block_unique").on(table.eventId, table.blockId),
]);

export type EventHomeworkBlockSelect = typeof EventHomeworkBlock.$inferSelect;

export const StudentFeedback = pgTable("student_feedbacks", {
  feedbackId: uuid("id").defaultRandom().primaryKey().notNull(),
  studentId: uuid("student_id").references(() => User.userId, { onDelete: 'cascade' }).notNull(),
  teacherId: uuid("teacher_id").references(() => User.userId, { onDelete: 'cascade' }).notNull(),
  type: text("type").$type<'midterm' | 'final'>().notNull(),
  dateFrom: date("date_from", { mode: "date" }).notNull(),
  dateTo: date("date_to", { mode: "date" }).notNull(),
  language: text("language").$type<'en' | 'pt'>().default('pt').notNull(),
  generatedContent: text("generated_content"),
  editedContent: text("edited_content"),
  status: text("status").$type<'pending' | 'generating' | 'generated' | 'published'>().default('pending').notNull(),
  publishedAt: timestamp("published_at", { mode: "date" }),
  createdAt: timestamp("created_at", { mode: "date" }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { mode: "date" }).defaultNow().notNull(),
});
export type StudentFeedbackSelect = typeof StudentFeedback.$inferSelect;
export type StudentFeedbackInsert = typeof StudentFeedback.$inferInsert;

// CEFR "can-do" abilities catalog — a flat, ordered list of statements per level.
// Used by the end-of-term (final) student feedback ability check.
export const Ability = pgTable("abilities", {
  abilityId: uuid("id").defaultRandom().primaryKey().notNull(),
  levelId: uuid("level_id")
    .references(() => Level.levelId, { onDelete: 'cascade' })
    .notNull(),
  text: text("text").notNull(), // the can-do statement
  sortOrder: integer("sort_order").notNull().default(0),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
});

export type AbilitySelect = typeof Ability.$inferSelect;
export type AbilityInsert = typeof Ability.$inferInsert;

// Matching exercise - connect prompts with their correct options via drag and drop
export const MatchingExercise = pgTable("matching_exercises", {
  matchingExerciseId: uuid("id").defaultRandom().primaryKey().notNull(),
  exerciseId: uuid("exercise_id")
    .references(() => Exercise.exerciseId, { onDelete: 'cascade' })
    .notNull(),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
});

export type MatchingExerciseSelect = typeof MatchingExercise.$inferSelect;

// Matching prompts - the left column items (stems)
export const MatchingPrompt = pgTable("matching_prompts", {
  matchingPromptId: uuid("id").defaultRandom().primaryKey().notNull(),
  matchingExerciseId: uuid("matching_exercise_id")
    .references(() => MatchingExercise.matchingExerciseId, { onDelete: 'cascade' })
    .notNull(),
  label: text("label").notNull(),
  order: integer("order").default(0).notNull(),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
});

export type MatchingPromptSelect = typeof MatchingPrompt.$inferSelect;

// Matching options - the right column items (matches)
export const MatchingOption = pgTable("matching_options", {
  matchingOptionId: uuid("id").defaultRandom().primaryKey().notNull(),
  matchingExerciseId: uuid("matching_exercise_id")
    .references(() => MatchingExercise.matchingExerciseId, { onDelete: 'cascade' })
    .notNull(),
  matchingPromptId: uuid("matching_prompt_id")
    .references(() => MatchingPrompt.matchingPromptId, { onDelete: 'cascade' })
    .notNull(),
  label: text("label").notNull(),
  order: integer("order").default(0).notNull(),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
});

export type MatchingOptionSelect = typeof MatchingOption.$inferSelect;

// Scramble exercise - unscramble words to form sentences
export const ScrambleExercise = pgTable("scramble_exercises", {
  scrambleExerciseId: uuid("id").defaultRandom().primaryKey().notNull(),
  exerciseId: uuid("exercise_id")
    .references(() => Exercise.exerciseId, { onDelete: 'cascade' })
    .notNull(),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
});

export type ScrambleExerciseSelect = typeof ScrambleExercise.$inferSelect;

// Scramble sentences - each sentence to be unscrambled
export const ScrambleSentence = pgTable("scramble_sentences", {
  scrambleSentenceId: uuid("id").defaultRandom().primaryKey().notNull(),
  scrambleExerciseId: uuid("scramble_exercise_id")
    .references(() => ScrambleExercise.scrambleExerciseId, { onDelete: 'cascade' })
    .notNull(),
  correctSentence: text("correct_sentence").notNull(),
  order: integer("order").default(0).notNull(),
  correction: text("correction"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
});

export type ScrambleSentenceSelect = typeof ScrambleSentence.$inferSelect;

// ==========================================
// COMMUNITY PLATFORM TABLES
// ==========================================

// User connections (following/followers)
export const UserConnection = pgTable("user_connections", {
  connectionId: uuid("id").defaultRandom().primaryKey().notNull(),
  followerId: uuid("follower_id").references(() => User.userId, { onDelete: 'cascade' }).notNull(),
  followingId: uuid("following_id").references(() => User.userId, { onDelete: 'cascade' }).notNull(),
  createdAt: timestamp("created_at", { mode: "date" }).defaultNow().notNull(),
}, (table) => [
  uniqueIndex("user_connections_follower_following_unique").on(table.followerId, table.followingId),
  index("user_connections_follower_idx").on(table.followerId),
  index("user_connections_following_idx").on(table.followingId),
]);

export type UserConnectionSelect = typeof UserConnection.$inferSelect;

// Badges - achievement definitions
export const Badge = pgTable("badges", {
  badgeId: uuid("id").defaultRandom().primaryKey().notNull(),
  name: text("name").notNull().unique(),
  description: text("description"),
  iconUrl: text("icon_url"),
  // Criteria for earning the badge (e.g., {type: 'posts_count', threshold: 50})
  criteria: jsonb("criteria").$type<{
    type: 'posts_count' | 'comments_count' | 'likes_received' | 'login_streak' | 'posts_pinned' | 'followers_count' | 'role';
    threshold?: number;
    role?: 'teacher' | 'moderator' | 'admin';
  }>(),
  createdAt: timestamp("created_at", { mode: "date" }).defaultNow().notNull(),
});

export type BadgeSelect = typeof Badge.$inferSelect;

// User badges - awarded badges
export const UserBadge = pgTable("user_badges", {
  userBadgeId: uuid("id").defaultRandom().primaryKey().notNull(),
  userId: uuid("user_id").references(() => User.userId, { onDelete: 'cascade' }).notNull(),
  badgeId: uuid("badge_id").references(() => Badge.badgeId, { onDelete: 'cascade' }).notNull(),
  earnedAt: timestamp("earned_at", { mode: "date" }).defaultNow().notNull(),
}, (table) => [
  uniqueIndex("user_badges_user_badge_unique").on(table.userId, table.badgeId),
  index("user_badges_user_idx").on(table.userId),
]);

export type UserBadgeSelect = typeof UserBadge.$inferSelect;

// Notifications - unified across community and school platforms
export const Notification = pgTable("notifications", {
  notificationId: uuid("id").defaultRandom().primaryKey().notNull(),
  userId: uuid("user_id").references(() => User.userId, { onDelete: 'cascade' }).notNull(),
  type: text("type").$type<
    // Community notifications
    | 'post_reply'
    | 'post_comment'
    | 'post_like'
    | 'comment_like'
    | 'mention'
    | 'post_pinned'
    | 'badge_earned'
    | 'level_up'
    | 'new_follower'
    // School platform notifications
    | 'lesson_assigned'
    | 'class_reminder'
    | 'assignment_graded'
    // A midterm/final feedback (scorecard) was published for the student.
    // payload: { feedbackId, feedbackType: 'midterm' | 'final' }
    | 'feedback_published'
    // A teacher left a personal comment on one of the student's exercise
    // submissions. payload: { submissionId, exerciseId, classId, className,
    // blockIdx, blockId, teacherName, commentPreview }
    | 'exercise_comment'
    // A message written by an admin and pushed from the admin app to many
    // students at once (see NotificationBroadcast). The text is stored as-is,
    // not templated. payload: { broadcastId, title, body, link }
    // (`link` is an in-app path like "/conquistas", a full https URL, or null)
    | 'admin_message'
    // Teacher-side: a student sent homework (exercise submissions) for one of
    // the teacher's classes. One row per (teacher, student, class) is kept
    // while unread; further submissions bump `count` on that row.
    // payload: { studentId, studentName, classId, className, blockIdx,
    //            exerciseId, submissionId, count, needsReview,
    //            kind: 'homework' | 'presentation' }
    | 'homework_submitted'
  >().notNull(),
  sourceService: text("source_service").$type<'community' | 'school' | 'graph'>().notNull(),
  // Service-specific data (post_id, comment_id, badge_id, etc.)
  payload: jsonb("payload").$type<Record<string, unknown>>().notNull(),
  read: boolean("read").default(false).notNull(),
  createdAt: timestamp("created_at", { mode: "date" }).defaultNow().notNull(),
}, (table) => [
  index("notifications_user_unread_idx").on(table.userId, table.read, table.createdAt),
]);

export type NotificationSelect = typeof Notification.$inferSelect;
export type NotificationType = NotificationSelect['type'];

// Point transactions - audit log for points
export const PointTransaction = pgTable("point_transactions", {
  transactionId: uuid("id").defaultRandom().primaryKey().notNull(),
  userId: uuid("user_id").references(() => User.userId, { onDelete: 'cascade' }).notNull(),
  actionType: text("action_type").$type<
    // Community actions
    | 'post_created'
    | 'comment_created'
    | 'like_received'
    | 'post_pinned'
    | 'space_joined'
    | 'first_post_bonus'
    // School actions
    | 'lesson_completed'
    | 'class_attended'
    | 'homework_completed'
    | 'daily_login'
  >().notNull(),
  pointsAwarded: integer("points_awarded").notNull(),
  sourceService: text("source_service").$type<'community' | 'school'>().notNull(),
  // Additional context (post_id, lesson_id, etc.)
  metadata: jsonb("metadata").$type<Record<string, unknown>>(),
  createdAt: timestamp("created_at", { mode: "date" }).defaultNow().notNull(),
}, (table) => [
  index("point_transactions_user_idx").on(table.userId, table.createdAt),
]);

export type PointTransactionSelect = typeof PointTransaction.$inferSelect;

// ==========================================
// COMMUNITY CONTENT TABLES
// ==========================================

// Spaces (discussion forums)
export const Space = pgTable("spaces", {
  spaceId: uuid("id").defaultRandom().primaryKey().notNull(),
  name: text("name").notNull().unique(),
  description: text("description"),
  slug: text("slug").notNull().unique(),
  createdByAdminId: uuid("created_by_admin_id").references(() => User.userId).notNull(),
  createdAt: timestamp("created_at", { mode: "date" }).defaultNow().notNull(),
});

export type SpaceSelect = typeof Space.$inferSelect;

// Posts
export const Post = pgTable("posts", {
  postId: uuid("id").defaultRandom().primaryKey().notNull(),
  spaceId: uuid("space_id").references(() => Space.spaceId, { onDelete: 'cascade' }).notNull(),
  authorId: uuid("author_id").references(() => User.userId, { onDelete: 'cascade' }).notNull(),
  type: text("type").$type<'discussion' | 'poll' | 'announcement'>().notNull().default('discussion'),
  title: text("title").notNull(),
  content: jsonb("content").$type<RichTextDoc>().notNull(),
  imageUrl: text("image_url"),
  tags: text("tags").array(),
  isPinned: boolean("is_pinned").default(false).notNull(),
  isLocked: boolean("is_locked").default(false).notNull(),
  // Cached engagement score for feed sorting (updated periodically)
  engagementScore: integer("engagement_score").default(0).notNull(),
  likesCount: integer("likes_count").default(0).notNull(),
  commentsCount: integer("comments_count").default(0).notNull(),
  createdAt: timestamp("created_at", { mode: "date" }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { mode: "date" }).defaultNow().notNull(),
}, (table) => [
  index("posts_space_idx").on(table.spaceId, table.createdAt),
  index("posts_author_idx").on(table.authorId, table.createdAt),
  index("posts_feed_idx").on(table.engagementScore, table.createdAt),
]);

export type PostSelect = typeof Post.$inferSelect;

// Comments
export const Comment = pgTable("comments", {
  commentId: uuid("id").defaultRandom().primaryKey().notNull(),
  postId: uuid("post_id").references(() => Post.postId, { onDelete: 'cascade' }).notNull(),
  authorId: uuid("author_id").references(() => User.userId, { onDelete: 'cascade' }).notNull(),
  content: jsonb("content").$type<RichTextDoc>().notNull(),
  likesCount: integer("likes_count").default(0).notNull(),
  createdAt: timestamp("created_at", { mode: "date" }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { mode: "date" }).defaultNow().notNull(),
}, (table) => [
  index("comments_post_idx").on(table.postId, table.createdAt),
]);

export type CommentSelect = typeof Comment.$inferSelect;

// Likes (polymorphic - for posts and comments)
export const Like = pgTable("likes", {
  likeId: uuid("id").defaultRandom().primaryKey().notNull(),
  targetType: text("target_type").$type<'post' | 'comment'>().notNull(),
  targetId: uuid("target_id").notNull(),
  userId: uuid("user_id").references(() => User.userId, { onDelete: 'cascade' }).notNull(),
  createdAt: timestamp("created_at", { mode: "date" }).defaultNow().notNull(),
}, (table) => [
  uniqueIndex("likes_target_user_unique").on(table.targetType, table.targetId, table.userId),
  index("likes_target_idx").on(table.targetType, table.targetId),
]);

export type LikeSelect = typeof Like.$inferSelect;

// Polls
export const Poll = pgTable("polls", {
  pollId: uuid("id").defaultRandom().primaryKey().notNull(),
  postId: uuid("post_id").references(() => Post.postId, { onDelete: 'cascade' }).notNull().unique(),
  question: text("question").notNull(),
  // Options stored as JSONB array: [{text: 'Option 1', voteCount: 0}, ...]
  options: jsonb("options").$type<{ text: string; voteCount: number }[]>().notNull(),
  endsAt: timestamp("ends_at", { mode: "date" }),
  createdAt: timestamp("created_at", { mode: "date" }).defaultNow().notNull(),
});

export type PollSelect = typeof Poll.$inferSelect;

// Poll votes
export const PollVote = pgTable("poll_votes", {
  pollVoteId: uuid("id").defaultRandom().primaryKey().notNull(),
  pollId: uuid("poll_id").references(() => Poll.pollId, { onDelete: 'cascade' }).notNull(),
  optionIndex: integer("option_index").notNull(),
  userId: uuid("user_id").references(() => User.userId, { onDelete: 'cascade' }).notNull(),
  createdAt: timestamp("created_at", { mode: "date" }).defaultNow().notNull(),
}, (table) => [
  uniqueIndex("poll_votes_poll_user_unique").on(table.pollId, table.userId),
]);

export type PollVoteSelect = typeof PollVote.$inferSelect;

// Direct messages
export const DirectMessage = pgTable("direct_messages", {
  messageId: uuid("id").defaultRandom().primaryKey().notNull(),
  senderId: uuid("sender_id").references(() => User.userId, { onDelete: 'cascade' }).notNull(),
  receiverId: uuid("receiver_id").references(() => User.userId, { onDelete: 'cascade' }).notNull(),
  message: text("message").notNull(),
  imageUrl: text("image_url"),
  readAt: timestamp("read_at", { mode: "date" }),
  createdAt: timestamp("created_at", { mode: "date" }).defaultNow().notNull(),
}, (table) => [
  index("dm_conversation_idx").on(table.senderId, table.receiverId, table.createdAt),
  index("dm_receiver_unread_idx").on(table.receiverId, table.readAt),
]);

export type DirectMessageSelect = typeof DirectMessage.$inferSelect;

// Reports
export const Report = pgTable("reports", {
  reportId: uuid("id").defaultRandom().primaryKey().notNull(),
  reporterId: uuid("reporter_id").references(() => User.userId, { onDelete: 'cascade' }).notNull(),
  targetType: text("target_type").$type<'post' | 'comment' | 'message' | 'user'>().notNull(),
  targetId: uuid("target_id").notNull(),
  reason: text("reason").$type<'spam' | 'harassment' | 'inappropriate' | 'misinformation' | 'other'>().notNull(),
  description: text("description"),
  status: text("status").$type<'pending' | 'reviewed' | 'resolved' | 'dismissed'>().default('pending').notNull(),
  moderatorNotes: text("moderator_notes"),
  resolvedById: uuid("resolved_by").references(() => User.userId),
  createdAt: timestamp("created_at", { mode: "date" }).defaultNow().notNull(),
  resolvedAt: timestamp("resolved_at", { mode: "date" }),
}, (table) => [
  index("reports_status_idx").on(table.status, table.createdAt),
]);

export type ReportSelect = typeof Report.$inferSelect;

// User blocks (user blocking another user)
export const UserBlock = pgTable("user_blocks", {
  userBlockId: uuid("id").defaultRandom().primaryKey().notNull(),
  blockerId: uuid("blocker_id").references(() => User.userId, { onDelete: 'cascade' }).notNull(),
  blockedId: uuid("blocked_id").references(() => User.userId, { onDelete: 'cascade' }).notNull(),
  createdAt: timestamp("created_at", { mode: "date" }).defaultNow().notNull(),
}, (table) => [
  uniqueIndex("user_blocks_blocker_blocked_unique").on(table.blockerId, table.blockedId),
  index("user_blocks_blocker_idx").on(table.blockerId),
]);

export type UserBlockSelect = typeof UserBlock.$inferSelect;

// Space subscriptions
export const SpaceSubscription = pgTable("space_subscriptions", {
  subscriptionId: uuid("id").defaultRandom().primaryKey().notNull(),
  spaceId: uuid("space_id").references(() => Space.spaceId, { onDelete: 'cascade' }).notNull(),
  userId: uuid("user_id").references(() => User.userId, { onDelete: 'cascade' }).notNull(),
  createdAt: timestamp("created_at", { mode: "date" }).defaultNow().notNull(),
}, (table) => [
  uniqueIndex("space_subscriptions_space_user_unique").on(table.spaceId, table.userId),
]);

export type SpaceSubscriptionSelect = typeof SpaceSubscription.$inferSelect;

// User bans
export const UserBan = pgTable("user_bans", {
  banId: uuid("id").defaultRandom().primaryKey().notNull(),
  userId: uuid("user_id").references(() => User.userId, { onDelete: 'cascade' }).notNull(),
  bannedBy: uuid("banned_by").references(() => User.userId).notNull(),
  reason: text("reason").notNull(),
  expiresAt: timestamp("expires_at", { mode: "date" }), // NULL = permanent ban
  createdAt: timestamp("created_at", { mode: "date" }).defaultNow().notNull(),
}, (table) => [
  index("user_bans_user_active_idx").on(table.userId, table.expiresAt),
]);

export type UserBanSelect = typeof UserBan.$inferSelect;

// User pinned spaces — tracks which spaces a user has pinned to their sidebar
export const UserPinnedSpace = pgTable("user_pinned_spaces", {
  userPinnedSpaceId: uuid("id").defaultRandom().primaryKey().notNull(),
  userId: uuid("user_id").references(() => User.userId, { onDelete: 'cascade' }).notNull(),
  spaceId: uuid("space_id").references(() => Space.spaceId, { onDelete: 'cascade' }).notNull(),
  position: integer("position").notNull(),
  createdAt: timestamp("created_at", { withTimezone: true, mode: "date" }).defaultNow().notNull(),
}, (table) => [
  uniqueIndex("user_pinned_spaces_user_space_unique").on(table.userId, table.spaceId),
  index("user_pinned_spaces_user_idx").on(table.userId, table.position),
]);

export type UserPinnedSpaceSelect = typeof UserPinnedSpace.$inferSelect;

// Space sub-pages — rich-text pages within a space (e.g. rules, resources, FAQ)
export const SpaceSubPage = pgTable("space_sub_pages", {
  subPageId: uuid("id").defaultRandom().primaryKey().notNull(),
  spaceId: uuid("space_id").references(() => Space.spaceId, { onDelete: 'cascade' }).notNull(),
  title: text("title").notNull(),
  bodyJson: jsonb("body_json").$type<Record<string, unknown>>(),
  bannerImageUrl: text("banner_image_url"),
  bannerVideoUrl: text("banner_video_url"),
  position: integer("position").notNull(),
  authorId: uuid("author_id").references(() => User.userId).notNull(),
  createdAt: timestamp("created_at", { withTimezone: true, mode: "date" }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true, mode: "date" }).defaultNow().notNull(),
}, (table) => [
  index("space_sub_pages_space_idx").on(table.spaceId, table.position),
]);

export type SpaceSubPageSelect = typeof SpaceSubPage.$inferSelect;

// Default pinned spaces — admin-curated defaults applied to new users on signup
export const DefaultPinnedSpace = pgTable("default_pinned_spaces", {
  defaultPinnedSpaceId: uuid("id").defaultRandom().primaryKey().notNull(),
  spaceId: uuid("space_id").references(() => Space.spaceId, { onDelete: 'cascade' }).notNull(),
  position: integer("position").notNull(),
  createdAt: timestamp("created_at", { withTimezone: true, mode: "date" }).defaultNow().notNull(),
}, (table) => [
  uniqueIndex("default_pinned_spaces_space_unique").on(table.spaceId),
]);

export type DefaultPinnedSpaceSelect = typeof DefaultPinnedSpace.$inferSelect;

// Moderator assignments — maps moderators to spaces they moderate
export const ModeratorAssignment = pgTable("moderator_assignments", {
  moderatorAssignmentId: uuid("id").defaultRandom().primaryKey().notNull(),
  userId: uuid("user_id").references(() => User.userId, { onDelete: 'cascade' }).notNull(),
  spaceId: uuid("space_id").references(() => Space.spaceId, { onDelete: 'cascade' }).notNull(),
  createdAt: timestamp("created_at", { withTimezone: true, mode: "date" }).defaultNow().notNull(),
}, (table) => [
  uniqueIndex("moderator_assignments_user_space_unique").on(table.userId, table.spaceId),
]);

export type ModeratorAssignmentSelect = typeof ModeratorAssignment.$inferSelect;

// Event notification log — tracks which notifications have been sent per event+rule for idempotency
export const EventNotificationLog = pgTable("event_notification_log", {
  id: uuid("id").defaultRandom().primaryKey(),
  eventId: uuid("event_id").references(() => Event.eventId, { onDelete: 'cascade' }).notNull(),
  ruleId: text("rule_id").notNull(),
  sentAt: timestamp("sent_at", { mode: "date", precision: 6, withTimezone: true }).defaultNow().notNull(),
  recipientsCount: integer("recipients_count").default(0).notNull(),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
}, (table) => ({
  uniqueEventRule: uniqueIndex("event_notification_log_event_rule_unique").on(table.eventId, table.ruleId),
}));

export type EventNotificationLogSelect = typeof EventNotificationLog.$inferSelect;

// Issue reports — captures user-submitted bug/issue reports from any app
export const IssueReport = pgTable("issue_reports", {
  id: uuid("id").defaultRandom().primaryKey().notNull(),
  userId: uuid("user_id").references(() => User.userId),
  userEmail: text("user_email").notNull(),
  description: text("description").notNull(),
  currentUrl: text("current_url").notNull(),
  userAgent: text("user_agent"),
  consoleErrors: text("console_errors"),
  app: text("app").$type<'admin' | 'p2e' | 'teacher' | 'community'>().notNull(),
  status: text("status").$type<'open' | 'resolved' | 'dismissed'>().default('open').notNull(),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
});

export type IssueReportSelect = typeof IssueReport.$inferSelect;

// ── Feedback Campaigns ──────────────────────────────────────────────────────

export const FeedbackCampaign = pgTable("feedback_campaigns", {
  campaignId: uuid("id").defaultRandom().primaryKey().notNull(),
  title: text("title").notNull(),
  description: text("description"),
  campaignType: text("campaign_type").$type<"general" | "event-linked">().notNull(),
  targetType: text("target_type").$type<"all" | "groups" | "users">().notNull(),
  eventId: uuid("event_id").references(() => Event.eventId),
  status: text("status").$type<"draft" | "active" | "closed">().default("draft").notNull(),
  isMandatory: boolean("is_mandatory").default(false).notNull(),
  createdByUserId: uuid("created_by_user_id").references(() => User.userId).notNull(),
  createdAt: timestamp("created_at", { mode: "date" }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { mode: "date" }).defaultNow().notNull(),
});

export type FeedbackCampaignSelect = typeof FeedbackCampaign.$inferSelect;

export const FeedbackCampaignQuestion = pgTable("feedback_campaign_questions", {
  questionId: uuid("id").defaultRandom().primaryKey().notNull(),
  campaignId: uuid("campaign_id").references(() => FeedbackCampaign.campaignId, { onDelete: 'cascade' }).notNull(),
  questionText: text("question_text").notNull(),
  questionType: text("question_type").$type<"rating" | "text">().default("rating").notNull(),
  order: integer("order").default(0).notNull(),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
});

export type FeedbackCampaignQuestionSelect = typeof FeedbackCampaignQuestion.$inferSelect;

export const FeedbackResponse = pgTable("feedback_responses", {
  responseId: uuid("id").defaultRandom().primaryKey().notNull(),
  campaignId: uuid("campaign_id").references(() => FeedbackCampaign.campaignId, { onDelete: 'cascade' }).notNull(),
  userId: uuid("user_id").references(() => User.userId, { onDelete: 'cascade' }).notNull(),
  eventId: uuid("event_id").references(() => Event.eventId),
  status: text("status").$type<"submitted" | "dismissed">().notNull(),
  submittedAt: timestamp("submitted_at", { mode: "date" }).defaultNow().notNull(),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
});

export type FeedbackResponseSelect = typeof FeedbackResponse.$inferSelect;

export const FeedbackResponseAnswer = pgTable("feedback_response_answers", {
  answerId: uuid("id").defaultRandom().primaryKey().notNull(),
  responseId: uuid("response_id").references(() => FeedbackResponse.responseId, { onDelete: 'cascade' }).notNull(),
  questionId: uuid("question_id").references(() => FeedbackCampaignQuestion.questionId, { onDelete: 'cascade' }).notNull(),
  rating: integer("rating"),
  textAnswer: text("text_answer"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
});

export type FeedbackResponseAnswerSelect = typeof FeedbackResponseAnswer.$inferSelect;

export const FeedbackCampaignGroup = pgTable("feedback_campaign_groups", {
  campaignId: uuid("campaign_id").references(() => FeedbackCampaign.campaignId, { onDelete: 'cascade' }).notNull(),
  groupId: uuid("group_id").references(() => Group.groupId, { onDelete: 'cascade' }).notNull(),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
});

export type FeedbackCampaignGroupSelect = typeof FeedbackCampaignGroup.$inferSelect;

export const FeedbackCampaignUser = pgTable("feedback_campaign_users", {
  campaignId: uuid("campaign_id").references(() => FeedbackCampaign.campaignId, { onDelete: 'cascade' }).notNull(),
  userId: uuid("user_id").references(() => User.userId, { onDelete: 'cascade' }).notNull(),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
});

export type FeedbackCampaignUserSelect = typeof FeedbackCampaignUser.$inferSelect;

// ── Announcements ───────────────────────────────────────────────────────────

/** Pop-up looks the student app can render; keep in sync with p2e `ANNOUNCEMENT_STYLE_NAMES`. */
export const ANNOUNCEMENT_STYLES = ["classic", "issue-cover", "wall-note", "school-letter", "extra-edition"] as const;
export type AnnouncementStyle = (typeof ANNOUNCEMENT_STYLES)[number];

export const Announcement = pgTable("announcements", {
  announcementId: uuid("id").defaultRandom().primaryKey().notNull(),
  title: text("title").notNull(),
  body: text("body"),
  mediaType: text("media_type").$type<"none" | "image" | "video" | "animation">().default("none").notNull(),
  mediaUrl: text("media_url"),
  posterUrl: text("poster_url"),
  ctaLabel: text("cta_label"),
  ctaTo: text("cta_to"),
  // "e2e" = English2Empower students only (group + private); leaves out b2b
  // (in-company) students, whose app hides journey features like the wall.
  targetType: text("target_type").$type<"all" | "e2e" | "groups" | "users">().notNull(),
  status: text("status").$type<"draft" | "active" | "closed">().default("draft").notNull(),
  skippable: boolean("skippable").default(true).notNull(),
  startsAt: timestamp("starts_at", { withTimezone: true, mode: "date" }),
  endsAt: timestamp("ends_at", { withTimezone: true, mode: "date" }),
  priority: integer("priority").default(0).notNull(),
  // Visual style of the student pop-up (apps/p2e announcements/styles).
  // null = the student app's default (today: "classic").
  style: text("style").$type<AnnouncementStyle>(),
  createdByUserId: uuid("created_by_user_id").references(() => User.userId).notNull(),
  createdAt: timestamp("created_at", { mode: "date" }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { mode: "date" }).defaultNow().notNull(),
});

export type AnnouncementSelect = typeof Announcement.$inferSelect;

export const AnnouncementGroup = pgTable("announcement_groups", {
  announcementId: uuid("announcement_id").references(() => Announcement.announcementId, { onDelete: 'cascade' }).notNull(),
  groupId: uuid("group_id").references(() => Group.groupId, { onDelete: 'cascade' }).notNull(),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
});

export type AnnouncementGroupSelect = typeof AnnouncementGroup.$inferSelect;

export const AnnouncementUser = pgTable("announcement_users", {
  announcementId: uuid("announcement_id").references(() => Announcement.announcementId, { onDelete: 'cascade' }).notNull(),
  userId: uuid("user_id").references(() => User.userId, { onDelete: 'cascade' }).notNull(),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
});

export type AnnouncementUserSelect = typeof AnnouncementUser.$inferSelect;

export const AnnouncementImpression = pgTable("announcement_impressions", {
  impressionId: uuid("id").defaultRandom().primaryKey().notNull(),
  announcementId: uuid("announcement_id").references(() => Announcement.announcementId, { onDelete: 'cascade' }).notNull(),
  userId: uuid("user_id").references(() => User.userId, { onDelete: 'cascade' }).notNull(),
  status: text("status").$type<"completed" | "dismissed">().notNull(),
  watchedSeconds: integer("watched_seconds"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
}, (table) => [
  uniqueIndex("announcement_impressions_announcement_user_unique").on(table.announcementId, table.userId),
]);

export type AnnouncementImpressionSelect = typeof AnnouncementImpression.$inferSelect;

// One row per "push notification" sent from the admin app (Announcements →
// Notifications tab). The actual bell entries are the per-recipient rows in
// `notifications` (type 'admin_message'); this table is the admin-side log.
export const NotificationBroadcast = pgTable("notification_broadcasts", {
  broadcastId: uuid("id").defaultRandom().primaryKey().notNull(),
  title: text("title").notNull(),
  body: text("body").notNull(),
  // In-app path ("/conquistas") or full https URL; null = no deep link.
  link: text("link"),
  audience: text("audience").$type<"all" | "groups" | "users">().notNull(),
  // Group ids (audience 'groups') or user ids (audience 'users'); [] for 'all'.
  targetIds: jsonb("target_ids").$type<string[]>().default([]).notNull(),
  recipientCount: integer("recipient_count").default(0).notNull(),
  createdByUserId: uuid("created_by_user_id").references(() => User.userId).notNull(),
  createdAt: timestamp("created_at", { mode: "date" }).defaultNow().notNull(),
});

export type NotificationBroadcastSelect = typeof NotificationBroadcast.$inferSelect;

// Per-user community preferences (e.g., popup/toast alerts).
// Default-on: absence of a row = popups enabled. Mute is opt-in.
export const CommunityUserPreferences = pgTable("community_user_preferences", {
  userId: uuid("user_id")
    .references(() => User.userId, { onDelete: 'cascade' })
    .primaryKey().notNull(),
  popupEnabled: boolean("popup_enabled").default(true).notNull(),
  updatedAt: timestamp("updated_at", { mode: "date" }).defaultNow().notNull(),
});

export type CommunityUserPreferencesSelect = typeof CommunityUserPreferences.$inferSelect;

// ── Achievements wall ────────────────────────────────────────────────────────
// Students' "small and big wins" wall (replaces the old Padlet). Anonymity is
// display-only: the author row is always kept for accountability. Posts are
// soft-deleted so removals by admins stay auditable.
export const AchievementPost = pgTable("achievement_posts", {
  achievementPostId: uuid("id").defaultRandom().primaryKey().notNull(),
  // NULL only for posts imported from the old Padlet wall.
  userId: uuid("user_id").references(() => User.userId, { onDelete: 'cascade' }),
  // Author name frozen at import time for legacy Padlet posts.
  legacyAuthorLabel: text("legacy_author_label"),
  isAnonymous: boolean("is_anonymous").default(false).notNull(),
  body: text("body").notNull(),
  deletedAt: timestamp("deleted_at", { withTimezone: true, mode: "date" }),
  createdAt: timestamp("created_at", { withTimezone: true, mode: "date" }).defaultNow().notNull(),
});

export type AchievementPostSelect = typeof AchievementPost.$inferSelect;

export const AchievementHeart = pgTable("achievement_hearts", {
  achievementPostId: uuid("achievement_post_id")
    .references(() => AchievementPost.achievementPostId, { onDelete: 'cascade' })
    .notNull(),
  userId: uuid("user_id").references(() => User.userId, { onDelete: 'cascade' }).notNull(),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
}, (table) => [
  uniqueIndex("achievement_hearts_post_user_unique").on(table.achievementPostId, table.userId),
]);

export type AchievementHeartSelect = typeof AchievementHeart.$inferSelect;

// ── Grammar Aid videos ───────────────────────────────────────────────────────
// Founder-recorded explainer videos, one per Grammar Aid topic (slug-keyed —
// the topic content itself ships with the frontend until the Phase-2 CMS).
export const GrammarTopicVideo = pgTable("grammar_topic_videos", {
  topicSlug: text("topic_slug").primaryKey().notNull(),
  videoUrl: text("video_url").notNull(),
  updatedByUserId: uuid("updated_by_user_id").references(() => User.userId),
  createdAt: timestamp("created_at", { withTimezone: true, mode: "date" }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true, mode: "date" }).defaultNow().notNull(),
});

export type GrammarTopicVideoSelect = typeof GrammarTopicVideo.$inferSelect;
